import { useState, useEffect } from 'react'

import styles from '~/bootcamp/components/StripeProgressBar/StripeProgressBar.module.css'

export enum ProgressBarSize {
  SM = 'small',
  MD = 'medium',
}

// Global state to persist progress across component unmounts/remounts
const globalProgressState: { [key: string]: number } = {}

export default function ProgressBar({
  className,
  backgroundColorClassName = 'bg-gray-light',
  durationClassName = 'duration-300',
  progressPercent,
  size = ProgressBarSize.SM,
  smoothAnimation = false,
  animationDuration = 1000,
  progressKey = 'default',
}: {
  className?: string
  backgroundColorClassName?: string
  durationClassName?: string
  progressPercent: number
  size?: ProgressBarSize
  smoothAnimation?: boolean
  animationDuration?: number
  progressKey?: string
}): React.ReactElement {
  const classNameProp = className ? className : `bg-purple ${styles['stripe-gradient']}`
  const [animatedProgress, setAnimatedProgress] = useState(() => globalProgressState[progressKey] ?? progressPercent)

  useEffect(() => {
    if (!smoothAnimation) {
      setAnimatedProgress(progressPercent)
      globalProgressState[progressKey] = progressPercent
      return
    }

    const targetProgress = progressPercent
    const startProgress = animatedProgress
    const difference = targetProgress - startProgress

    if (Math.abs(difference) < 0.1) {
      setAnimatedProgress(targetProgress)
      globalProgressState[progressKey] = targetProgress
      return
    }

    const startTime = Date.now()
    const animate = (): void => {
      const elapsed = Date.now() - startTime
      const progressRatio = Math.min(elapsed / animationDuration, 1)
      const easeOutCubic = 1 - Math.pow(1 - progressRatio, 3)
      const newProgress = startProgress + difference * easeOutCubic

      setAnimatedProgress(newProgress)
      globalProgressState[progressKey] = newProgress

      if (progressRatio < 1) {
        requestAnimationFrame(animate)
      } else {
        setAnimatedProgress(targetProgress)
        globalProgressState[progressKey] = targetProgress
      }
    }

    requestAnimationFrame(animate)
  }, [progressPercent, smoothAnimation, animationDuration, progressKey, animatedProgress])

  const displayProgress = smoothAnimation ? animatedProgress : progressPercent
  const transitionClass = smoothAnimation ? 'duration-0' : durationClassName

  return (
    <div
      className={`relative rounded ${backgroundColorClassName} shadow-inner-lite z-1 ${size === ProgressBarSize.SM ? 'h-2' : 'h-3'}`}
    >
      <div className={`h-full rounded transition-all ${transitionClass}`} style={{ width: `${displayProgress}%` }}>
        <div className={`w-full h-full rounded ${classNameProp}`} />
      </div>
    </div>
  )
}
